import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react';
import React, { useEffect, useState } from 'react';

import { GalleryImage } from './types';

interface ThumbProps {
  selected: boolean;
  imgSrc: string;
  onClick: () => void;
  index: number;
  alt: string;
  videoLabel?: string;
}

function Thumb({
  index,
  selected,
  imgSrc,
  onClick,
  alt,
  videoLabel,
}: ThumbProps) {
  return (
    <div
      className={`flex-[0_0_30%] min-w-0 transform-gpu ${
        index !== 0 && 'pl-1'
      } relative opacity-60 ${selected ? '!opacity-100' : ''}`}
    >
      <button
        onClick={onClick}
        className="bg-transparent touch-manipulation block no-underline cursor-pointer border-0 p-0 m-0 w-full  "
        type="button"
      >
        <div className="w-10 mobile:w-6 h-10 mobile:h-6 mobile:text-sm mobile:font-semibold z-1 absolute top-1 right-1 rounded-full bg-black opacity-50 leading-[40px] mobile:leading-6 font-semibold text-center pointer-events-none ">
          <span className={'z-2 text-white opacity-100'}>{index + 1}</span>
        </div>
        <img
          className="rounded-lg block h-36 object-cover mobile:h-24 w-full"
          src={imgSrc}
          alt={alt}
        />
      </button>
      {videoLabel && (
        <div
          className={
            'text-white text-xs absolute bottom-3 left-3 p-1 px-2 bg-black bg-opacity-50 rounded-2xl'
          }
        >
          {videoLabel}
        </div>
      )}
    </div>
  );
}

interface Props {
  images: GalleryImage[];
  alt: string;
  options?: EmblaOptionsType;
  /** Shown as the first slide, played inline once it's selected. */
  video?: { url: string; thumbnailUrl: string };
  videoLabel?: string;
  /** Re-measures the carousel when it changes, e.g. the route. */
  resetKey?: string;
  className?: string;
  /** Called with the index in images, not counting the video. */
  onImageClick?: (index: number) => void;
}

/**
 * Carousel for a single listing: one large image at a time with a draggable
 * strip of numbered thumbnails below it.
 */
function GalleryCarousel({
  images,
  options,
  alt,
  video,
  videoLabel = 'Lokalvisning',
  resetKey,
  className = 'w-[1400px]',
  onImageClick,
}: Props) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
    ...options,
  });
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });
  const offset = video ? 1 : 0;

  const onThumbClick = (index: number) => {
    if (!emblaMainApi || !emblaThumbsApi) {
      return;
    }

    emblaMainApi.scrollTo(index);
  };

  const onSelect = () => {
    if (!emblaMainApi || !emblaThumbsApi) {
      return;
    }
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  };

  useEffect(() => {
    if (!emblaMainApi) {
      return;
    }

    onSelect();
    emblaMainApi.on('select', onSelect);
    emblaMainApi.on('reInit', onSelect);
  }, [emblaMainApi]);
  useEffect(() => {
    emblaMainApi?.reInit();
  }, [resetKey]);

  return (
    <div
      className={`rounded-lg overflow-hidden ${className}`}
      key={images.map(img => img.id).join(',')}
    >
      <div className="overflow-hidden transform-gpu" ref={emblaMainRef}>
        <div className="flex h-auto gap-2">
          {video && (
            <div className="flex-[0_0_100%] rounded-lg w-full m-mobile:h-96 min-w-0 relative transform-gpu">
              {selectedIndex === 0 ? (
                <video
                  className="w-full h-full"
                  controls
                  preload="none"
                  poster={video.thumbnailUrl}
                >
                  <source src={video.url} type="video/mp4" />
                  Your browser does not support HTML5 video.
                </video>
              ) : (
                <img
                  className="rounded-lg overflow-hidden block h-full mobile:aspect-[3/2] w-full"
                  src={video.thumbnailUrl}
                  alt={`${alt} – video`}
                />
              )}
            </div>
          )}
          {images.map((image, index) => {
            const imageAlt = index === 0 ? alt : `${alt} – bild ${index + 1}`;
            return (
              <div
                className="flex-[0_0_100%] m-mobile:h-96 min-w-0 relative transform-gpu"
                key={image.id}
              >
                <img
                  srcSet={
                    image.mobileUrl
                      ? `${image.mobileUrl} 640w, ${image.url} 1024w`
                      : undefined
                  }
                  sizes="(max-width: 640px) 640px, 1024px"
                  className={`rounded-lg overflow-hidden block h-full mobile:aspect-[3/2] w-full ${
                    onImageClick ? 'cursor-pointer' : ''
                  }`}
                  src={image.url}
                  alt={imageAlt}
                  onClick={onImageClick && (() => onImageClick(index))}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-1 overflow-x-hidden">
        <div ref={emblaThumbsRef}>
          <div className="flex ml-[calc(2 * -1)]">
            {video && (
              <Thumb
                key={'video-thumbnail'}
                alt={`${alt} – video`}
                index={0}
                onClick={() => {
                  emblaMainApi?.reInit();
                  onThumbClick(0);
                }}
                selected={selectedIndex === 0}
                imgSrc={video.thumbnailUrl}
                videoLabel={videoLabel}
              />
            )}
            {images.map((image, i) => {
              const index = i + offset;
              const thumbAlt = index === 0 ? alt : `${alt} – bild ${index + 1}`;
              return (
                <Thumb
                  key={image.id}
                  alt={thumbAlt}
                  index={index}
                  onClick={() => {
                    emblaMainApi?.reInit();
                    onThumbClick(index);
                    setSelectedIndex(index);
                  }}
                  selected={index === selectedIndex}
                  imgSrc={image.thumbnailUrl ?? image.url}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GalleryCarousel;
